import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";

export default function ContactForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("contact.required");
    if (!form.email.trim()) e.email = t("contact.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = t("contact.invalidEmail");
    if (!form.message.trim()) e.message = t("contact.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // frontend-only, redirect to Spanish-only thank you page
    setTimeout(() => {
      navigate("/thank_you");
    }, 200);
  };

  const fieldCls = (name) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-[14px] text-[#0F1B2D] placeholder:text-[#4b5563]/60 outline-none transition ${
      errors[name]
        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
        : "border-[#0F1B2D]/15 focus:border-[#0F1B2D] focus:ring-2 focus:ring-[#0F1B2D]/10"
    }`;

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form" noValidate>
      <div>
        <label htmlFor="cf-name" className="mb-1.5 block text-[13px] font-medium text-[#0F1B2D]">
          {t("contact.name")}
        </label>
        <input
          id="cf-name"
          name="name"
          type="text"
          value={form.name}
          onChange={onChange}
          placeholder={t("contact.namePlaceholder")}
          className={fieldCls("name")}
          data-testid="cf-name"
          autoComplete="name"
        />
        {errors.name && (
          <p className="mt-1 text-[12px] text-red-600" data-testid="cf-name-error">{errors.name}</p>
        )}
      </div>
      <div>
        <label htmlFor="cf-email" className="mb-1.5 block text-[13px] font-medium text-[#0F1B2D]">
          {t("contact.email")}
        </label>
        <input
          id="cf-email"
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          placeholder={t("contact.emailPlaceholder")}
          className={fieldCls("email")}
          data-testid="cf-email"
          autoComplete="email"
        />
        {errors.email && (
          <p className="mt-1 text-[12px] text-red-600" data-testid="cf-email-error">{errors.email}</p>
        )}
      </div>
      <div>
        <label htmlFor="cf-message" className="mb-1.5 block text-[13px] font-medium text-[#0F1B2D]">
          {t("contact.message")}
        </label>
        <textarea
          id="cf-message"
          name="message"
          rows={5}
          value={form.message}
          onChange={onChange}
          placeholder={t("contact.messagePlaceholder")}
          className={fieldCls("message") + " resize-none"}
          data-testid="cf-message"
        />
        {errors.message && (
          <p className="mt-1 text-[12px] text-red-600" data-testid="cf-message-error">{errors.message}</p>
        )}
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="cf-submit"
        className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#0F1B2D] px-6 text-[13.5px] font-semibold text-[#F7F3EA] hover:bg-[#1c2c46] transition-colors disabled:opacity-70"
      >
        <Send className="h-4 w-4" />
        {t("contact.submit")}
      </button>
    </form>
  );
}
